import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Link } from 'react-router-dom';
import { HelpCircle, X, ChevronRight, Stethoscope, Users, CalendarDays, Wallet, Activity, ClipboardList, FileSignature, Receipt } from 'lucide-react';

/**
 * Floating help button with quick-start guide for admin & patients.
 */
export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  if (!user) return null;

  const adminGuide = [
    { icon: Users, title: 'Manage patients', desc: 'Patients → Open chart, run interview, set treatment plan, discharge.' },
    { icon: CalendarDays, title: 'Book appointments', desc: 'Appointments → New → choose patient + service → Save. Auto-jumps to that day.' },
    { icon: Activity, title: 'Exercise database', desc: 'Exercise Library → New (or use AI suggestions) → assign exercises to patients.' },
    { icon: Wallet, title: 'Invoices', desc: 'Finances → New invoice. VAT-exempt (PL Art. 43(1)(19)). Print, export PDF, or mark paid.' },
    { icon: ClipboardList, title: 'Patient interview', desc: 'Patients → Interview button. Mark pain points on the body chart, set ICD-10/ICF codes.' },
    { icon: FileSignature, title: 'Treatment plan', desc: 'Patient chart → Treatment plan. Patient signs from their portal.' },
  ];

  const patientGuide = [
    { icon: CalendarDays, title: 'Your appointments', desc: 'Appointments → cancel a slot or pay a 25% deposit (if enabled).' },
    { icon: ClipboardList, title: 'Pre-visit interview', desc: 'Interview → fill your symptoms, mark pain on the body chart.' },
    { icon: FileSignature, title: 'Sign treatment plan', desc: 'Treatment plan → review and sign with your finger or mouse.' },
    { icon: Receipt, title: 'Invoices', desc: 'Invoices → download a Polish-format PDF; pay via BLIK or cash at the clinic.' },
    { icon: Activity, title: 'Exercises', desc: 'Exercises → see prescribed routines and the full library.' },
  ];

  const guide = user.role === 'admin' ? adminGuide : patientGuide;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-30 w-12 h-12 rounded-full bg-brand text-white shadow-lg hover:scale-105 transition print:hidden flex items-center justify-center"
        data-testid="help-button"
        title="Quick help"
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4" data-testid="help-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[85vh] overflow-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Stethoscope className="w-4 h-4 text-brand" />
                  <span className="text-xs uppercase tracking-[0.2em] text-accent font-medium">QUICK START</span>
                </div>
                <h3 className="font-heading font-bold text-2xl">{user.role === 'admin' ? 'Admin guide' : 'Patient guide'}</h3>
                <p className="text-fg-muted text-sm mt-1">Logged in as <span className="font-medium text-fg">{user.email}</span></p>
              </div>
              <button onClick={() => setOpen(false)} className="text-fg-muted hover:text-fg p-1" data-testid="help-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              {guide.map((g, i) => (
                <div key={i} className="surface border border-soft rounded-lg p-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[#F2EBDB] flex items-center justify-center flex-shrink-0">
                    <g.icon className="w-4 h-4 text-brand" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-sm">{g.title}</div>
                    <div className="text-xs text-fg-secondary mt-0.5">{g.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {user.role === 'admin' ? (
              <Link to="/admin" onClick={() => setOpen(false)}
                    className="mt-4 btn-primary w-full inline-flex items-center justify-center gap-2 text-sm" data-testid="help-go-admin">
                Open admin dashboard <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <Link to="/patient" onClick={() => setOpen(false)}
                    className="mt-4 btn-primary w-full inline-flex items-center justify-center gap-2 text-sm" data-testid="help-go-patient">
                Open patient dashboard <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
}
